<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import paymentService from '../services/payment.service'

const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const clubUuid = computed(() => authStore.initData?.club?.uuid || null)

// Check permissions
const hasPermission = (permission: string) => {
  const permissions = authStore.initData?.permission_model || []
  return permissions.includes(permission)
}

const canViewPayments = computed(() => {
  return hasPermission('PAYMENT_INDEX')
})

// Data
const recurringPayments = ref<any[]>([])
const loading = ref(false)
const currentPage = ref(1)
const perPage = ref(50)
const total = ref(0)
const lastPage = ref(1)
const fromItem = computed(() => total.value > 0 ? (currentPage.value - 1) * perPage.value + 1 : 0)
const toItem = computed(() => Math.min(currentPage.value * perPage.value, total.value))

// Expanded accordion items (mobile)
const expandedItems = ref<Set<string>>(new Set())

// Dropdown states
const openDropdown = ref<string | null>(null)

// Delete modal
const showDeleteModal = ref(false)
const paymentToDelete = ref<any>(null)

// Period type translation
const getPeriodText = (periodType: string) => {
  const key = `recurringPayments.period_${periodType}`
  const translated = t(key)
  return translated !== key ? translated : periodType
}

// Format date to YYYY-MM-DD
const formatDate = (dateString: string) => {
  if (!dateString) return ''
  return dateString.split('T')[0]
}

// Fetch recurring payments
const fetchRecurringPayments = async () => {
  if (!clubUuid.value) return

  loading.value = true
  try {
    const response = await paymentService.getRecurringPayments(clubUuid.value, {
      page: currentPage.value,
      per_page: perPage.value
    })

    recurringPayments.value = response.data || []
    currentPage.value = response.current_page || 1
    perPage.value = response.per_page || 50
    total.value = response.total || 0
    lastPage.value = response.last_page || 1
  } catch (error: any) {
    console.error('Error fetching recurring payments:', error)
    toastStore.error(t('recurringPayments.errorFetchingPayments'))
  } finally {
    loading.value = false
  }
}

// Pagination
const changePage = (page: number) => {
  if (page < 1 || page > lastPage.value) return
  currentPage.value = page
  fetchRecurringPayments()
}

const prevPage = () => {
  if (currentPage.value > 1) changePage(currentPage.value - 1)
}

const nextPage = () => {
  if (currentPage.value < lastPage.value) changePage(currentPage.value + 1)
}

const changePerPage = () => {
  currentPage.value = 1
  fetchRecurringPayments()
}

const visiblePages = computed(() => {
  const maxVisible = 5
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  let end = Math.min(lastPage.value, start + maxVisible - 1)
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1)
  }
  const pages: number[] = []
  for (let i = start; i <= end; i++) pages.push(i)
  return pages
})

// Toggle accordion
const toggleAccordion = (uuid: string) => {
  if (expandedItems.value.has(uuid)) {
    expandedItems.value.delete(uuid)
  } else {
    expandedItems.value.add(uuid)
  }
}

const isExpanded = (uuid: string) => {
  return expandedItems.value.has(uuid)
}

// Toggle dropdown
const toggleDropdown = (event: Event, uuid: string) => {
  event.stopPropagation()
  if (openDropdown.value === uuid) {
    openDropdown.value = null
  } else {
    openDropdown.value = uuid
  }
}

const isDropdownOpen = (uuid: string) => {
  return openDropdown.value === uuid
}

// Close dropdown when clicking outside
const closeAllDropdowns = () => {
  openDropdown.value = null
}

// Open delete modal
const openDeleteModal = (payment: any) => {
  paymentToDelete.value = payment
  showDeleteModal.value = true
  openDropdown.value = null
}

// Close delete modal
const closeDeleteModal = () => {
  paymentToDelete.value = null
  showDeleteModal.value = false
}

// Delete recurring payment
const deleteRecurringPayment = async () => {
  if (!clubUuid.value || !paymentToDelete.value) return

  try {
    await paymentService.deleteRecurringPayment(clubUuid.value, paymentToDelete.value.uuid)
    toastStore.success(t('recurringPayments.paymentDeletedSuccessfully'))
    closeDeleteModal()
    fetchRecurringPayments()
  } catch (error: any) {
    console.error('Error deleting recurring payment:', error)
    const errorMessage = error.response?.data?.message || t('recurringPayments.errorDeletingPayment')
    toastStore.error(errorMessage)
  }
}

onMounted(async () => {
  if (!canViewPayments.value) {
    toastStore.error(t('recurringPayments.noPermission'))
    router.push('/payments')
    return
  }

  document.addEventListener('click', closeAllDropdowns)
  await fetchRecurringPayments()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Back Button -->
      <div class="mb-4">
        <router-link to="/payments" class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('recurringPayments.backToList') }}
        </router-link>
      </div>

      <!-- Page Header -->
      <div class="mb-4 md:mb-4 mb-2 flex justify-between items-center">
        <h1 class="text-2xl font-bold text-gray-900">
          {{ t('recurringPayments.title') }} <span class="text-lg font-normal text-gray-600">({{ t('recurringPayments.found') }}: {{ total }})</span>
        </h1>
        <router-link to="/payments/add" class="px-4 py-2 md:px-4 md:py-2 px-2 py-1 bg-green-600 text-sm md:text-sm text-xs font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 flex items-center gap-2 md:gap-2 gap-1">
          <svg class="w-4 h-4 md:w-4 md:h-4 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
          </svg>
          <span class="md:inline hidden">{{ t('recurringPayments.addPayment') }}</span>
          <span class="md:hidden inline">{{ t('recurringPayments.add') }}</span>
        </router-link>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p class="mt-2 text-gray-600">{{ t('recurringPayments.loading') }}</p>
      </div>

      <!-- Desktop Table -->
      <div v-if="!loading" class="hidden md:block bg-white border-2 border-gray-300 rounded shadow-sm">
        <div style="overflow-x: clip; overflow-y: visible;">
          <table class="w-full text-sm">
            <thead class="bg-gray-100 border-b-2 border-gray-300">
              <tr>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('recurringPayments.paymentName') }}</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('recurringPayments.amount') }}</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('recurringPayments.firstPayment') }}</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('recurringPayments.daysToPay') }}</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('recurringPayments.period') }}</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700 lg:table-cell hidden">{{ t('recurringPayments.playerCount') }}</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700 lg:hidden">{{ t('recurringPayments.playerCountShort') }}</th>
                <th class="px-4 py-3 text-right font-semibold text-gray-700 sticky right-0 bg-gray-100"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-if="recurringPayments.length === 0">
                <td colspan="9" class="px-4 py-8 text-center text-gray-500">
                  {{ t('recurringPayments.noPayments') }}
                </td>
              </tr>
              <tr v-for="(payment, index) in recurringPayments" :key="payment.uuid" class="hover:bg-gray-50">
                <td class="px-4 py-3 text-gray-900 font-medium">{{ payment.name }}</td>
                <td class="px-4 py-3 text-gray-700 font-semibold">{{ parseFloat(payment.amount).toFixed(2) }} {{ payment.currency || 'PLN' }}</td>
                <td class="px-4 py-3 text-gray-700">{{ formatDate(payment.first_payment_date) }}</td>
                <td class="px-4 py-3 text-gray-700">{{ payment.days_to_pay }}</td>
                <td class="px-4 py-3 text-gray-700">{{ getPeriodText(payment.period_type) }}</td>
                <td class="px-4 py-3 text-gray-700">{{ payment.players_count || 0 }}</td>
                <td class="px-4 py-3 sticky right-0 bg-inherit text-right" style="position: relative;">
                  <div class="flex items-center gap-1 justify-end">
                    <button @click="toggleDropdown($event, `desktop-${payment.uuid}`)" class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300">
                      {{ t('recurringPayments.actions') }}
                    </button>
                    <div :class="['custom-dropdown', 'dropdown-bottom', { 'show': isDropdownOpen(`desktop-${payment.uuid}`) }]">
                      <router-link :to="`/recurring-payments/${payment.uuid}/edit`">{{ t('recurringPayments.edit') }}</router-link>
                      <button class="delete-action" @click="openDeleteModal(payment)">{{ t('recurringPayments.delete') }}</button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Mobile Accordion -->
      <div v-if="!loading" class="md:hidden space-y-2">
        <div v-if="recurringPayments.length === 0" class="bg-white border-2 border-gray-300 rounded shadow-sm p-6 text-center text-gray-500">
          {{ t('recurringPayments.noPayments') }}
        </div>
        <div v-for="payment in recurringPayments" :key="payment.uuid" class="bg-white border-2 border-gray-300 rounded shadow-sm">
          <!-- Accordion Header -->
          <div class="p-3 cursor-pointer" @click="toggleAccordion(payment.uuid)">
            <!-- Above 500px: single row layout -->
            <div class="hidden min-[500px]:flex items-center justify-between">
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-gray-900">{{ payment.name }}</div>
              </div>
              <div class="flex items-center gap-2 ml-2">
                <div class="text-sm font-semibold text-gray-900">
                  {{ payment.players_count || 0 }} x {{ parseFloat(payment.amount).toFixed(2) }}
                </div>
                <div class="relative">
                  <button @click="toggleDropdown($event, `mobile-${payment.uuid}`)" class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300">
                    {{ t('recurringPayments.actions') }}
                  </button>
                  <div :class="['custom-dropdown', 'dropdown-bottom', { 'show': isDropdownOpen(`mobile-${payment.uuid}`) }]">
                    <router-link :to="`/recurring-payments/${payment.uuid}/edit`">{{ t('recurringPayments.edit') }}</router-link>
                    <button class="delete-action" @click="openDeleteModal(payment)">{{ t('recurringPayments.delete') }}</button>
                  </div>
                </div>
                <svg :class="['w-5 h-5 text-gray-600 transition-transform', isExpanded(payment.uuid) ? 'rotate-180' : '']" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
                </svg>
              </div>
            </div>

            <!-- Below 500px: stacked layout -->
            <div class="flex min-[500px]:hidden flex-col gap-2">
              <div class="text-sm font-medium text-gray-900">{{ payment.name }}</div>
              <div class="flex items-center justify-between">
                <div class="text-xs font-semibold text-gray-900">
                  {{ payment.players_count || 0 }} x {{ parseFloat(payment.amount).toFixed(2) }}
                </div>
                <div class="flex items-center gap-2">
                  <div class="relative">
                    <button @click="toggleDropdown($event, `mobile-small-${payment.uuid}`)" class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300">
                      {{ t('recurringPayments.actions') }}
                    </button>
                    <div :class="['custom-dropdown', 'dropdown-bottom', { 'show': isDropdownOpen(`mobile-small-${payment.uuid}`) }]">
                      <router-link :to="`/recurring-payments/${payment.uuid}/edit`">{{ t('recurringPayments.edit') }}</router-link>
                      <button class="delete-action" @click="openDeleteModal(payment)">{{ t('recurringPayments.delete') }}</button>
                    </div>
                  </div>
                  <svg :class="['w-5 h-5 text-gray-600 transition-transform', isExpanded(payment.uuid) ? 'rotate-180' : '']" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <!-- Accordion Content -->
          <div v-if="isExpanded(payment.uuid)" class="border-t-2 border-gray-300 p-3">
            <div class="space-y-2 text-xs">
              <div class="flex justify-between">
                <span class="font-semibold text-gray-700">{{ t('recurringPayments.amount') }}:</span>
                <span class="text-gray-600 font-semibold">{{ parseFloat(payment.amount).toFixed(2) }} {{ payment.currency || 'PLN' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="font-semibold text-gray-700">{{ t('recurringPayments.firstPayment') }}:</span>
                <span class="text-gray-600">{{ formatDate(payment.first_payment_date) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="font-semibold text-gray-700">{{ t('recurringPayments.daysToPay') }}:</span>
                <span class="text-gray-600">{{ payment.days_to_pay }}</span>
              </div>
              <div class="flex justify-between">
                <span class="font-semibold text-gray-700">{{ t('recurringPayments.period') }}:</span>
                <span class="text-gray-600">{{ getPeriodText(payment.period_type) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="font-semibold text-gray-700">{{ t('recurringPayments.playerCount') }}:</span>
                <span class="text-gray-600">{{ payment.players_count || 0 }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && total > 0" class="mt-4 flex flex-col lg:flex-row justify-between items-center gap-4">
        <div class="text-sm text-gray-700 w-full lg:w-auto text-center lg:text-left">
          {{ t('recurringPayments.displaying') }} {{ fromItem }} - {{ toItem }} {{ t('recurringPayments.of') }} {{ total }} {{ t('recurringPayments.recurringPaymentsWord') }}
        </div>
        <div class="flex flex-col md:flex-row items-center gap-4 w-full lg:w-auto">
          <div class="flex items-center gap-2 w-full md:w-auto justify-center md:justify-start">
            <label for="perPage" class="text-sm text-gray-700">{{ t('recurringPayments.perPage') }}</label>
            <select id="perPage" v-model.number="perPage" @change="changePerPage" class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="75">75</option>
              <option :value="100">100</option>
            </select>
          </div>
          <div class="flex items-center gap-1 w-full md:w-auto justify-center md:justify-start">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &laquo;
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="changePage(page)"
              :class="[
                'px-3 py-1 border-2 rounded text-sm',
                page === currentPage
                  ? 'border-gray-900 bg-gray-900 text-white font-medium'
                  : 'border-gray-300 bg-white hover:bg-gray-50'
              ]"
            >
              {{ page }}
            </button>
            <button
              @click="nextPage"
              :disabled="currentPage === lastPage"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" @click="closeDeleteModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div @click.stop class="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div class="p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('recurringPayments.deleteModalTitle') }}</h2>
          <p class="text-gray-700 mb-6">
            {{ t('recurringPayments.deleteModalConfirm') }} "<span class="font-semibold">{{ paymentToDelete?.name }}</span>"?
          </p>

          <div class="flex gap-3 justify-end">
            <button @click="closeDeleteModal" class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300">
              {{ t('recurringPayments.cancel') }}
            </button>
            <button @click="deleteRecurringPayment" class="px-4 py-2 bg-red-600 text-sm font-medium text-white rounded hover:bg-red-700">
              {{ t('recurringPayments.delete') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
